import { useEffect, useRef, useState } from 'react'
import { appConfig } from '../config/app.config'

const labels: Record<string, string> = {
  'started-as-scheduled': 'Started as Scheduled',
  'no-show': 'No Show',
  'will-start-later': 'Will Start Later',
}

interface StarterResponse {
  staffName: string
  action: string
  alreadyRecorded: boolean
  originalStartDate?: string
  newStartDate?: string | null
}

export function sydneyToday() {
  const parts = new Intl.DateTimeFormat('en-AU', {
    timeZone: 'Australia/Sydney', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date())
  return ['year', 'month', 'day'].map((type) => parts.find((part) => part.type === type)!.value).join('-')
}

export function validCalendarDate(value: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const [year, month, day] = value.split('-').map(Number)
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)
  return year > 0 && month >= 1 && month <= 12 && day >= 1 &&
    day <= [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1]
}

export default function NewStarterResponse() {
  const [result, setResult] = useState<StarterResponse | null>(null)
  const [recorded, setRecorded] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [newStartDate, setNewStartDate] = useState('')
  const submitting = useRef(false)
  const [params] = useState(() => {
    const search = new URLSearchParams(window.location.search)
    return Object.fromEntries(
      ['token', 'contactId', 'date', 'action'].map((key) => [key, search.get(key) || ''])
    )
  })
  const originalStartDate = result?.originalStartDate || params.date
  const delayed = result?.action === 'will-start-later'

  useEffect(() => {
    document.title = 'New starter | Duo Hub'
    const referrer = document.createElement('meta')
    referrer.name = 'referrer'
    referrer.content = 'no-referrer'
    document.head.appendChild(referrer)
    const controller = new AbortController()
    async function load() {
      try {
        const response = await fetch(
          `${appConfig.urls.backend}/api/new-starters/respond?${new URLSearchParams(params)}`,
          {
            signal: controller.signal,
            credentials: 'omit',
            referrerPolicy: 'no-referrer',
            cache: 'no-store',
          }
        )
        const data = await response.json()
        if (!response.ok)
          throw new Error(data.error || 'Unable to load the response. Please contact HR.')
        setResult(data)
        setRecorded(data.alreadyRecorded)
      } catch (err) {
        if (!controller.signal.aborted)
          setError(err instanceof Error ? err.message : 'Unable to load the response.')
      }
    }
    void load()
    return () => {
      controller.abort()
      referrer.remove()
    }
  }, [params])

  async function confirm() {
    if (submitting.current || recorded) return
    if (delayed && (!validCalendarDate(newStartDate) || newStartDate <= originalStartDate || newStartDate < sydneyToday())) {
      setError('Choose a valid new start date later than the original start date and not before today in Sydney.')
      return
    }
    submitting.current = true
    setBusy(true)
    setError('')
    try {
      const response = await fetch(`${appConfig.urls.backend}/api/new-starters/respond`, {
        method: 'POST',
        credentials: 'omit',
        referrerPolicy: 'no-referrer',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...params, ...(delayed ? { newStartDate } : {}) }),
      })
      const data = await response.json()
      if (!response.ok)
        throw new Error(data.error || 'Unable to confirm the response. Please try again.')
      if (data.action === 'will-start-later' && !data.alreadyRecorded && !validCalendarDate(data.newStartDate || ''))
        throw new Error('The date update is currently unavailable. Please retry the same date or contact HR.')
      setResult(data)
      setRecorded(true)
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Unable to confirm the response. Please try again.'
      )
    } finally {
      submitting.current = false
      setBusy(false)
    }
  }

  return (
    <main style={{ maxWidth: 440, margin: '12vh auto', padding: '24px', color: '#293d57' }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 16 }}>
        {recorded ? 'Response recorded' : 'Confirm start status'}
      </h1>
      {result && (
        <p role="status" style={{ lineHeight: 1.6 }}>
          <strong>{result.staffName}</strong>
          <br />
          {labels[result.action]}
        </p>
      )}
      {!result && !error && <p role="status">Loading…</p>}
      {result && <p style={{ marginTop: 16 }}>Original start date: {originalStartDate}</p>}
      {recorded && result?.newStartDate && <p style={{ marginTop: 16 }}>New start date: {result.newStartDate}</p>}
      {result && delayed && !recorded && (
        <div style={{ marginTop: 20 }}>
          <label htmlFor="new-start-date" style={{ display: 'block', fontWeight: 600 }}>New start date (required)</label>
          <p id="new-start-date-help" style={{ margin: '8px 0', lineHeight: 1.6 }}>
            Confirming changes this staff member’s Salesforce join date. Choose a date after {originalStartDate} and not before today in Sydney.
            {' '}If an update fails, retry the same date or contact HR.
          </p>
          <input id="new-start-date" type="date" required disabled={busy}
            aria-describedby="new-start-date-help" value={newStartDate}
            min={sydneyToday()} onChange={(event) => setNewStartDate(event.target.value)}
            style={{ padding: '10px', border: '1px solid #8895a5', borderRadius: 6, width: '100%' }} />
        </div>
      )}
      {error && (
        <p role="alert" style={{ marginTop: 16 }}>
          {error}
        </p>
      )}
      {result && !recorded && (
        <button
          type="button"
          disabled={busy}
          onClick={() => void confirm()}
          style={{
            marginTop: 24,
            background: '#fa6400',
            color: '#fff',
            borderRadius: 24,
            padding: '12px 28px',
            fontWeight: 600,
            opacity: busy ? 0.6 : 1,
          }}
        >
          {busy ? 'Confirming…' : 'Confirm'}
        </button>
      )}
    </main>
  )
}
